import { Component, OnDestroy, OnInit } from '@angular/core';

// RxJS
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// CDK
import { Clipboard } from '@angular/cdk/clipboard';

// Services
import { AlertService } from '@modules/elements/services/alert.service';
import { NotificationService } from '@modules/elements/services/notification.service';
import { UsersService } from '@modules/users/services/users.service';

// Types
import { ApiClient, ApiClientScope } from '@modules/users/types/api-client';
import { SettingsService } from '@modules/settings/services/settings.service';

interface ScopeOption {
  key: ApiClientScope;
  label: string;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.less',
  standalone: false
})
export class SettingsComponent implements OnInit, OnDestroy {

  // Public — list
  public keys: ApiClient[] = [];
  public revealedSecret?: { id?: string; name?: string; apiKey: string };

  // Public — create form
  public form = {
    name: '',
    scopes: new Set<ApiClientScope>(['records:read']),
    expiresAt: '', // yyyy-mm-dd, blank = no expiration
  };
  public scopeOptions: ScopeOption[] = [
    { key: 'records:read',  label: 'Read records' },
    { key: 'records:write', label: 'Write records' },
  ];

  // Private
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    private usersService: UsersService,
    private alertService: AlertService,
    private notificationService: NotificationService,
    private clipboard: Clipboard,
    private settingsService: SettingsService
  ) { }

  /**
   * View lifecycle
   */

  ngOnInit(): void {
    this.loadKeys();
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  loadKeys(): void {
    this.usersService.getApiKeys()
      .pipe(takeUntil(this.alive))
      .subscribe(keys => this.keys = keys || []);
  }

  toggleScope(scope: ApiClientScope): void {
    if (this.form.scopes.has(scope)) {
      this.form.scopes.delete(scope);
    } else {
      this.form.scopes.add(scope);
    }
  }

  isScopeSelected(scope: ApiClientScope): boolean {
    return this.form.scopes.has(scope);
  }

  canCreate(): boolean {
    return this.form.name.trim().length > 0 && this.form.scopes.size > 0;
  }

  create(): void {
    if (!this.canCreate()) {
      return;
    }
    const payload = new ApiClient();
    payload.name = this.form.name.trim();
    payload.scopes = Array.from(this.form.scopes);
    if (this.form.expiresAt) {
      payload.expiresAt = new Date(this.form.expiresAt);
    }
    this.usersService.createApiKey(payload)
      .pipe(takeUntil(this.alive))
      .subscribe(created => {
        if (!created?.apiKey) {
          this.notificationService.error('Create API key', 'Server did not return a key');
          return;
        }
        this.revealedSecret = { id: created.id, name: created.name, apiKey: created.apiKey };
        this.resetForm();
        this.loadKeys();
      });
  }

  copy(value?: string): void {
    if (!value) {
      return;
    }
    this.clipboard.copy(value);
    this.notificationService.success('Copied to clipboard', '', false);
  }

  rotate(key: ApiClient): void {
    if (!key.id) {
      return;
    }
    this.alertService.show(
      'Rotate API key',
      `Rotating "${key.name}" will invalidate the current secret immediately. Continue?`,
      ['Rotate', 'Cancel'],
      0,
    )
    .pipe(takeUntil(this.alive))
    .subscribe(response => {
      if (response !== 0 || !key.id) {
        return;
      }
      this.usersService.rotateApiKey(key.id)
        .pipe(takeUntil(this.alive))
        .subscribe(updated => {
          if (updated?.apiKey) {
            this.revealedSecret = { id: key.id, name: key.name, apiKey: updated.apiKey };
          }
          this.loadKeys();
        });
    });
  }

  revoke(key: ApiClient): void {
    if (!key.id || key.status === 'revoked') {
      return;
    }
    this.alertService.show(
      'Revoke API key',
      `Revoke "${key.name}"? This blocks the key but keeps the record for audit.`,
      ['Revoke', 'Cancel'],
      0,
    )
    .pipe(takeUntil(this.alive))
    .subscribe(response => {
      if (response !== 0 || !key.id) {
        return;
      }
      this.usersService.revokeApiKey(key.id)
        .pipe(takeUntil(this.alive))
        .subscribe(() => this.loadKeys());
    });
  }

  delete(key: ApiClient): void {
    if (!key.id) {
      return;
    }
    this.alertService.show(
      'Delete API key',
      `Permanently delete "${key.name}"? This cannot be undone.`,
      ['Delete', 'Cancel'],
      0,
    )
    .pipe(takeUntil(this.alive))
    .subscribe(response => {
      if (response !== 0 || !key.id) {
        return;
      }
      this.usersService.deleteApiKey(key.id)
        .pipe(takeUntil(this.alive))
        .subscribe(() => {
          if (this.revealedSecret?.id === key.id) {
            this.revealedSecret = undefined;
          }
          this.loadKeys();
        });
    });
  }

  dismissSecret(): void {
    this.revealedSecret = undefined;
  }

  scopeLabel(scope: ApiClientScope): string {
    return this.scopeOptions.find(option => option.key === scope)?.label || scope;
  }

  /**
   * Helpers
   */

  private resetForm(): void {
    this.form.name = '';
    this.form.scopes = new Set<ApiClientScope>(['records:read']);
    this.form.expiresAt = '';
  }

}
